import React from 'react';
import {
  IconMenuTrigger,
  Menu,
  MenuContent,
  MenuItem,
  MoreIcon,
} from '@packages/systems/spring';
import {EList, ERecord, EText} from '@packages/systems/wfdl-ast';
import {
  type ContextualExpression,
  ContextualExpressions,
  getAnn,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import type {ExpressionEditorDispatch} from '@packages/systems/ExpressionEditor-utils';
import type {ListActionControlsType} from '@packages/systems/ExpressionEditor-custom-controls';
import {Plugins, Types} from '@packages/systems/wfdl';
import {namesEqual, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {emptyArray} from '@packages/utilities/fp/utils';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {selectSiteId} from '@packages/systems/designer/site-data';

import {
  BulkAddListItemsModal,
  BULK_ADD_OPTIONS_LABEL,
} from './BulkAddListItemsModal';
import {
  trackBulkSelectOptionsAdded,
  trackBulkSelectOptionsModalOpened,
} from './bulkAddSelectOptionsAnalytics';
import type {BulkListItem} from './parseBulkListItems';

const formSelectOptionType = qualify(
  Plugins.name('Form'),
  typeName('FormSelectOption')
);

const isSelectOptionsList = (expr: ContextualExpression) => {
  const dataType = getAnn(expr).type;
  const itemType = dataType ? Types.getListItemType(dataType) : null;
  const itemTypeName = itemType ? Types.getTypeConstructor(itemType) : null;
  if (!itemTypeName) return false;
  return namesEqual(itemTypeName, formSelectOptionType);
};

type Props = {
  /** The Choices list (`form.opts`) of the Select field. */
  expression: ContextualExpression;
  /** Dispatch scoped to the Choices list. */
  dispatch: ExpressionEditorDispatch;
};

/**
 * Secondary entry point for the Select field's Choices list: opens a modal for
 * pasting many `Text, Value` lines at once. The `+` button it sits next to
 * remains the primary, single-option action.
 */
export const BulkAddSelectOptions = ({expression, dispatch}: Props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const siteId = useDesignerSelector(selectSiteId);

  const handleOpen = () => {
    trackBulkSelectOptionsModalOpened(siteId);
    setIsOpen(true);
  };

  // Appends every pasted option in a single EXPRESSION_ACTION so the whole
  // batch is one undo step.
  const handleCreate = (
    parsedItems: Array<BulkListItem>,
    {rawText}: {rawText: string}
  ) => {
    const currentItems =
      ContextualExpressions.getList(stripAnn(expression)) || emptyArray;
    dispatch({
      key: emptyArray,
      type: 'EXPRESSION_ACTION',
      payload: {
        newExpression: EList([
          ...currentItems.map((item) => stripAll(item)),
          ...parsedItems.map(({text, value}) =>
            ERecord({t: EText(text), v: EText(value)})
          ),
        ]),
      },
    });
    trackBulkSelectOptionsAdded({siteId, items: parsedItems, rawText});
  };

  return (
    <>
      <Menu placement="bottom-end">
        <IconMenuTrigger
          Icon={MoreIcon}
          label="More add options"
          withTooltip
          variant="ghost"
          density="compact"
          data-automation-id="form-select-bulk-add-trigger"
        />
        <MenuContent>
          <MenuItem
            onClick={handleOpen}
            data-automation-id="form-select-bulk-add-menu-item"
          >
            {BULK_ADD_OPTIONS_LABEL}
          </MenuItem>
        </MenuContent>
      </Menu>
      <BulkAddListItemsModal
        isOpen={isOpen}
        onCreate={handleCreate}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

/**
 * Contributes the bulk add control to the header row of the Select field's
 * Choices list. Gated on the list's item type so it can only ever render on a
 * list of `FormSelectOption`s, no matter how widely it gets provided.
 */
export const bulkAddSelectOptionsAction: ListActionControlsType[number] = {
  id: 'form-select-bulk-add-options',
  condition: (expr, dispatch) =>
    isSelectOptionsList(expr) ? (
      <BulkAddSelectOptions expression={expr} dispatch={dispatch} />
    ) : null,
};

export const bulkAddSelectOptionsListActions: ListActionControlsType = [
  bulkAddSelectOptionsAction,
];
